import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import SystemHealthReportViewer from './SystemHealthReportViewer';

// Initialize Supabase client
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

interface ReportRun {
  run_id: string;
  env: string;
  generated_at: string;
  overall_status: string;
  processed_count: number;
  success_rate: string;
  p95_processing_ms: number;
  queue_depth: number;
  json_path: string;
}

const AdminReportsSection: React.FC = () => {
  const [reports, setReports] = useState<ReportRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [viewerOpen, setViewerOpen] = useState(false);

  // Fetch reports from database
  const fetchReports = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('system_health_report_runs')
        .select('*')
        .order('generated_at', { ascending: false })
        .limit(20);

      if (error) throw error;
      setReports(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'OK': return 'bg-green-100 text-green-800';
      case 'WARN': return 'bg-yellow-100 text-yellow-800';
      case 'CRIT': return 'bg-red-100 text-red-800';
      case 'NO_DATA': return 'bg-gray-100 text-gray-800';
      case 'STALE_DATA': return 'bg-orange-100 text-orange-800';
      case 'INSUFFICIENT_DATA': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // View report (fetch JSON and display in modal)
  const viewReport = async (jsonPath: string) => {
    try {
      const { data, error } = await supabase.storage
        .from('system-reports')
        .download(jsonPath);

      if (error) throw error;

      const jsonText = await data.text();
      const report = JSON.parse(jsonText);

      // Open in modal viewer
      setSelectedReport(report);
      setViewerOpen(true);
    } catch (err: any) {
      alert(`Error loading report: ${err.message}`);
    }
  };

  // Refresh Now - generate new report
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // TODO: Call Netlify Function to generate new report
      alert('Refresh functionality will be implemented in Phase 6');
    } catch (err: any) {
      alert(`Error refreshing: ${err.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-2 text-slate-600">Loading reports...</p>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">Admin Reports</h2>
            <p className="text-sm text-slate-500">System health monitoring (Internal use only)</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 bg-brand-blue text-white rounded-md hover:bg-brand-blue/90 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            Error: {error}
          </div>
        )}

        {reports.length === 0 ? (
          <p className="text-slate-500">No reports available</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Generated</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Processed</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Success Rate</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">P95 (ms)</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-200">
                {reports.map((report) => (
                  <tr key={report.run_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-900">
                      {new Date(report.generated_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(report.overall_status)}`}>
                        {report.overall_status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-900">{report.processed_count}</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{report.success_rate}%</td>
                    <td className="px-4 py-3 text-sm text-slate-900">{report.p95_processing_ms.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">
                      <button
                        onClick={() => viewReport(report.json_path)}
                        className="text-brand-blue hover:text-brand-blue/80 font-medium"
                      >
                        View Report
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Report Viewer Modal */}
      {viewerOpen && selectedReport && (
        <SystemHealthReportViewer
          report={selectedReport}
          onClose={() => {
            setViewerOpen(false);
            setSelectedReport(null);
          }}
        />
      )}
    </>
  );
};

export default AdminReportsSection;

